import React, { useState, useEffect } from "react";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";

// Step 2 of password recovery (code-only). The user enters their email,
// the 6-digit code we emailed, and a new password. Email is prefilled
// from ?email=… when ForgotPassword forwards it, but stays editable.
const ResetPassword = (props) => {
  const search =
    (props.location && props.location.search) ||
    (typeof window !== "undefined" ? window.location.search : "");
  const emailFromUrl = new URLSearchParams(search).get("email") || "";

  const [email, setEmail] = useState(emailFromUrl);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // After a successful reset, bounce to /login so they can sign in.
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => navigate("/login"), 1800);
    return () => clearTimeout(t);
  }, [success]);

  const validate = () => {
    if (!email) return "Enter the email you registered with.";
    if (!/^\d{6}$/.test(otp.trim())) return "Enter the 6-digit code from your email.";
    if (!password || password.length < 6)
      return "Password must be at least 6 characters.";
    if (password !== confirm) return "Passwords do not match.";
    return null;
  };

  const handleSubmit = (resetPassword) => {
    const err = validate();
    if (err) {
      setMessage(err);
      return;
    }
    setIsSubmitting(true);
    setMessage("");
    resetPassword({
      email: email.trim().toLowerCase(),
      otp: otp.trim(),
      newPassword: password,
    })
      .then(() => {
        setIsSubmitting(false);
        setSuccess(true);
      })
      .catch((e) => {
        setIsSubmitting(false);
        const msg =
          e?.response?.data?.message ||
          "Couldn't reset your password. The code may have expired.";
        setMessage(msg);
      });
  };

  return (
    <AuthConsumer>
      {({ resetPassword }) => (
        <div className="theme-scientific auth-page">
          <div className="auth-shell auth-shell--solo">
            <section className="auth-form-panel">
              <header className="auth-form-panel__header">
                <div className="sci-eyebrow">ICMOBI / Set a new password</div>
                <h2 className="auth-form-panel__title">
                  {success ? "Password updated" : "Choose a new password"}
                </h2>
                <p className="auth-form-panel__subtitle">
                  {success
                    ? "You're all set. Redirecting you to sign in…"
                    : "Enter your email, the 6-digit code we sent, and a new password."}
                </p>
              </header>

              {message && (
                <div className="sci-alert sci-alert--danger">
                  <div className="sci-alert__icon" />
                  <span>{message}</span>
                </div>
              )}

              {success ? (
                <button
                  className="sci-btn sci-btn--primary sci-btn--full"
                  style={{ marginTop: 8 }}
                  onClick={() => navigate("/login")}
                >
                  Go to sign in →
                </button>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(resetPassword);
                  }}
                >
                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="rp-email"
                    >
                      Email
                    </label>
                    <input
                      id="rp-email"
                      type="email"
                      className="sci-input"
                      placeholder="you@institution.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="rp-otp"
                    >
                      6-digit code
                    </label>
                    <input
                      id="rp-otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="sci-input"
                      placeholder="••••••"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      autoComplete="one-time-code"
                    />
                  </div>

                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="rp-pass"
                    >
                      New password
                    </label>
                    <input
                      id="rp-pass"
                      type="password"
                      className="sci-input"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="rp-pass2"
                    >
                      Confirm new password
                    </label>
                    <input
                      id="rp-pass2"
                      type="password"
                      className="sci-input"
                      placeholder="••••••••"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>

                  <button
                    type="submit"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    disabled={isSubmitting}
                    style={{ marginTop: 8 }}
                  >
                    {isSubmitting ? "Resetting…" : "Reset password →"}
                  </button>
                </form>
              )}

              <div className="auth-footer-link">
                <span>Need a new code?</span>
                <a
                  href="/forgot-password"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/forgot-password");
                  }}
                >
                  Request another →
                </a>
              </div>
            </section>
          </div>
        </div>
      )}
    </AuthConsumer>
  );
};

export default ResetPassword;
